/* eslint-disable max-len */
import React from 'react';
import { useProductPreview } from '../../hooks';
import './style.scss';

const renderImageIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#80CCCB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
    <circle cx="8.5" cy="8.5" r="1.5"></circle>
    <polyline points="21 15 16 10 5 21"></polyline>
  </svg>
);

const renderPackageIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E8971A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
  </svg>
);

export default function ProductPreview({ onTotalChange }) {
  const {
    displayItems,
    quantities,
    selectedChoices,
    subChoiceQuantities,
    handleQuantityChange,
    handleSelectChoice,
    handleToggleMultipleChoice,
    handleStepMultipleChoice,
  } = useProductPreview(onTotalChange);

  return (
    <div className="review-product-card">
      <div className="review-product-header">
        <span className="header-title">ITEMS</span>
        <span className="header-subtitle">{displayItems.length} product(s)</span>
      </div>

      <div className="review-product-list">
        {displayItems.map((item) => {
          const qty = quantities[item.id] || 0;

          return (
            <div key={item.id} className="review-product-item">
              <div className="item-content">
                <div
                  className="item-image"
                  style={{
                    background: item.photoUrl
                      ? '#f8fafc'
                      : item.isPackageIcon
                      ? '#FEF2DC'
                      : '#E0F5F5',
                  }}
                >
                  {item.photoUrl ? (
                    <img src={item.photoUrl} alt={item.name} />
                  ) : item.isPackageIcon ? (
                    renderPackageIcon()
                  ) : (
                    renderImageIcon()
                  )}
                </div>

                <div className="item-info">
                  <div className="item-title-row">
                    <div className="item-name">{item.name}</div>
                    <div className="item-price">${item.unitPrice.toFixed(2)}</div>
                  </div>

                  {item.description && (
                    <div className="item-description">{item.description}</div>
                  )}

                  {item.isSplitCaseItem && item.splitCaseDetails && (
                    <div className="case-fill-section">
                      <div className="case-fill-header">
                        <span>Case fill</span>
                        <span className="fill-count">
                          {item.splitCaseDetails.currentFill} / {item.splitCaseDetails.itemsPerCase}
                        </span>
                      </div>
                      <div className="case-fill-bar">
                        <div
                          className="case-fill-progress"
                          style={{
                            width: `${Math.min(
                              100,
                              (item.splitCaseDetails.currentFill / (item.splitCaseDetails.itemsPerCase || 1)) * 100
                            )}%`,
                          }}
                        />
                      </div>
                      <div className="case-fill-need">
                        Need {item.splitCaseDetails.needMore} more to fulfill
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {item.subGroups && item.subGroups.length > 0 && (
                <div className="item-subgroups-container">
                  {item.subGroups.map((group, gIdx) => {
                    if (group.selectionMode === 'multiple') {
                      return (
                        <div key={gIdx} className="sub-item-multiple-container">
                          <div className="sub-item-multiple-header">
                            {group.type ? group.type.toUpperCase() : 'OPTIONS'}{' '}
                            <span className="header-hint">· Pick any</span>
                          </div>
                          <div className="sub-item-multiple-rows">
                            {group.choices?.map((choice) => {
                              const choiceQty = subChoiceQuantities[item.id]?.[gIdx]?.[choice.index] || 0;
                              const isSelected = choiceQty > 0;
                              const displayPrice = `+$${choice.price.toFixed(2)}`;

                              return (
                                <div key={choice.index} className="bt-row">
                                  <div
                                    className={`bt-chk ${isSelected ? 'on' : ''}`}
                                    onClick={() => handleToggleMultipleChoice(item.id, gIdx, choice.index)}
                                    role="button"
                                    tabIndex={0}
                                  >
                                    {isSelected && (
                                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </div>
                                  <span className="bt-name">{choice.name}</span>
                                  <span className="bt-price-lbl">{displayPrice}</span>
                                  <div className="bt-stepper">
                                    <button
                                      type="button"
                                      className="bt-sbtn minus"
                                      disabled={!isSelected || choiceQty <= 0}
                                      onClick={() => handleStepMultipleChoice(item.id, gIdx, choice.index, -1)}
                                    >
                                      −
                                    </button>
                                    <span
                                      className="bt-val"
                                      style={{ color: isSelected ? '#1a1a18' : '#B4B2A9' }}
                                    >
                                      {choiceQty}
                                    </span>
                                    <button
                                      type="button"
                                      className="bt-sbtn plus"
                                      disabled={!isSelected}
                                      onClick={() => handleStepMultipleChoice(item.id, gIdx, choice.index, 1)}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={gIdx} className="item-tags-container">
                        <div className="item-tags-row">
                          {group.type ? (
                            <span className="sub-item-type-label">
                              {group.type}:
                            </span>
                          ) : null}
                          {group.choices?.map((choice) => {
                            const isSelected = (selectedChoices[item.id]?.[gIdx] || []).includes(choice.index);
                            const displayText = choice.price > 0
                              ? `${choice.name} +$${choice.price.toFixed(2)}`
                              : choice.name;

                            return (
                              <span
                                key={choice.index}
                                className={`tag-pill ${isSelected ? 'tag-pill--active' : ''}`}
                                onClick={() => handleSelectChoice(item.id, gIdx, choice.index, group.selectionMode)}
                                role="button"
                                tabIndex={0}
                              >
                                {displayText}
                              </span>
                            );
                          })}
                          {group.isRequired && (
                            <span className="tag-required">Required</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="item-footer">
                <div className="orders-count">
                  {item.ordersMade} order(s) made
                </div>
                <div className="quantity-controls">
                  <button
                    type="button"
                    className="btn-qty btn-minus"
                    onClick={() => handleQuantityChange(item.id, -1)}
                    disabled={qty <= 0}
                  >
                    −
                  </button>
                  <span className="qty-value">{qty}</span>
                  <button
                    type="button"
                    className="btn-qty btn-plus"
                    onClick={() => handleQuantityChange(item.id, 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
